import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NameFields from "@/components/NameFields";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POSITIONS = ["PG", "SG", "SF", "PF", "C"];

export default function RegisterPlayer() {
    const navigate = useNavigate();
    const { registerPlayer } = useAuth();
    const [form, setForm] = useState({
        first_name: "", middle_initial: "", last_name: "", email: "", password: "",
        age: 16, grad_year: 2028, position: "PG", height_inches: 72,
        home_zip: "", max_travel_distance: 300, bio: "",
    });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const onNumber = (k) => (e) => setForm({ ...form, [k]: Number(e.target.value) });

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.first_name.trim() || !form.last_name.trim()) { setError("First and last name are required"); return; }
        const mi = form.middle_initial ? `${form.middle_initial}. ` : "";
        const name = `${form.first_name.trim()} ${mi}${form.last_name.trim()}`.trim();
        setSubmitting(true);
        const { first_name, middle_initial, last_name, ...rest } = form;
        const res = await registerPlayer({
            ...rest,
            name,
            age: Number(form.age),
            grad_year: Number(form.grad_year),
            height_inches: Number(form.height_inches),
            max_travel_distance: Number(form.max_travel_distance),
        });
        setSubmitting(false);
        if (res.ok) navigate("/profile");
        else setError(res.error);
    };

    return (
        <div className="max-w-2xl mx-auto px-6 py-12">
            <div className="text-[#ff4500] text-xs uppercase tracking-[0.3em] mb-2">Player signup</div>
            <h1 className="font-display text-5xl tracking-wide">Build your profile</h1>
            <p className="text-sm text-[#a0a0a0] mt-2">
                Teams will discover you based on position, age, and how far you'll travel.{" "}
                <Link to="/register" className="text-[#ff4500] hover:underline">Wrong type?</Link>
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-player-form">
                <NameFields testidPrefix="player" section="player" first={form.first_name} mi={form.middle_initial} last={form.last_name} onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
                <Field label="Email" type="email" testid="player-email" value={form.email} onChange={onChange("email")} required autoComplete="email" />
                <p className="text-[10px] text-[#ff4500] -mt-2">Your email can't be changed later.</p>
                <Field label="Password (min 6 chars)" type="password" testid="player-password" value={form.password} onChange={onChange("password")} required minLength={6} />

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Age" type="number" testid="player-age" value={form.age} onChange={onNumber("age")} required min={10} max={25} />
                    <Field label="Grad Year" type="number" testid="player-grad" value={form.grad_year} onChange={onNumber("grad_year")} required min={2024} max={2035} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Position</Label>
                        <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                            <SelectTrigger data-testid="player-position" className="mt-2 h-12 rounded-none bg-[#141414] border-[#2a2a2a]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none">
                                {POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Field label="Height (inches)" type="number" testid="player-height" value={form.height_inches} onChange={onNumber("height_inches")} required min={48} max={96} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Home ZIP (5 digits)" testid="player-zip" value={form.home_zip} onChange={onChange("home_zip")} required pattern="[0-9]{5}" />
                    <Field label="Max Travel (miles)" type="number" testid="player-maxtravel" value={form.max_travel_distance} onChange={onNumber("max_travel_distance")} required min={0} max={3000} />
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">Bio (optional)</Label>
                    <Textarea
                        data-testid="player-bio"
                        value={form.bio}
                        onChange={onChange("bio")}
                        rows={3}
                        placeholder="Tell scouts about your game…"
                        className="mt-2 rounded-none bg-[#141414] border-[#2a2a2a]"
                    />
                </div>

                {error && <div data-testid="player-error" className="text-sm text-[#ff4500] border border-[#ff4500] p-3">{error}</div>}

                <Button type="submit" disabled={submitting} data-testid="player-submit" className="w-full h-12 rounded-none bg-[#ff4500] hover:bg-[#ff5722] uppercase tracking-[0.2em] text-xs font-semibold">
                    {submitting ? "Creating…" : "Create Player Profile"}
                </Button>
            </form>
        </div>
    );
}

function Field({ label, testid, ...props }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</Label>
            <Input data-testid={testid} {...props} className="mt-2 h-12 rounded-none bg-[#141414] border-[#2a2a2a]" />
        </div>
    );
}
